import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { AppState } from '@toro/common/shared/interfaces/app-state.interface';

import { OperationType } from '@toro/common/shared/enum/orders/operation-type.enum';

import { StockCheckoutType } from '@toro/common/shared/models/stocks-exchange/stock-checkout.model';
import { ResearchAdviceLite } from '@toro/common/shared/models/research/research-opportunity.model';

import { ResearchActions } from '@toro/common/shared/state-management/research/research.action';
import { StocksMarketAction } from '@toro/common/shared/state-management/stocks-market/stocks-market.action';

import { ResearchEffects } from '@toro/common/shared/state-management/research/research.effects';
import { StocksMarketEffects } from '@toro/common/shared/state-management/stocks-market/stocks-market.effects';
import { ApplicationMessagesAction } from '@toro/common/shared/state-management/application-messages/application-message.action';


@Injectable({providedIn: 'root'})
export class CheckoutService {

    constructor(
        public researchEffects: ResearchEffects,
        public stocksMarketEffects: StocksMarketEffects,
        private store: Store<AppState>
    ){}

    /**
     * Function that get stockCheckout param data from exchangeCheckout store
     */
    public getStockItemStoreData(): Observable<StockCheckoutType> {
        return this.store.select((state) => state.exchangeCheckout.stockCheckout);
    }
    
    /**
     * Function that dispatch a getOpportunity action in ResearchActions
     * @param adviceId Opportunity identifier
     */
    public requestOpportunityData(adviceId: number) {
        this.store.dispatch({ type: ResearchActions.getOpportunity, payload: adviceId });
    }

    /**
     * Function that get opportunity param data from research store
     * @param adviceId Opportunity identifier
     */
    public getOpportunityStoreData(adviceId: number): Observable<ResearchAdviceLite> {
        return this.store.select((state) => state.research.opportunity)
                .pipe(filter((opportunity) => opportunity && opportunity.id === adviceId))
    }

    /**
     * 
     * Function that get opportunityError data from research store
     */
    public getOpportunityStoreErrorData(): Observable<any> {
        return this.researchEffects.getOpportunity$
            .pipe(filter((item) => (item.type as any).equals(ResearchActions.getOpportunityError)))
    }

    /**
     * @param stockId Stock Identifier
     * @param isDayTrade Info that indicate if is a daytrade priceBand
     */
    public requestPriceBandData(stockId: string, isDayTrade: boolean) {
        const payloadPriceBand = { symbol : stockId, operationType: isDayTrade ? OperationType.DayTrade : OperationType.Position };
        this.store.dispatch({ type: ResearchActions.getPriceBands, payload: payloadPriceBand});
    }

    /**
     * Function that get priceBand param data from research store
     * @param stockId Stock Identifier
     */
    public getPriceBandStoreData(stockId: string): Observable<any> {
        return this.store.select((state) => state.research.priceBand)
            .pipe(
                filter((priceBand) => priceBand[stockId]),
                map((priceBand) => priceBand[stockId])
            )
    }

    /**
     * 
     * Function that get priceBandError data from research store
     */
    public getPriceBandStoreErrorData(): Observable<any> {
        return this.researchEffects.getPriceBands$
            .pipe(filter((item) => (item.type as any).equals(ResearchActions.getPriceBandsError)))
    }

    /**
     * Function that dispatch a getInstrumentInfo action in StocksMarketAction
     * @param stockId Stock Identifier
     */
    public requestInstrumentData(stockId: string, isFractionary: boolean){
        if (isFractionary) this.store.dispatch({type: StocksMarketAction.getInstrumentInfo, payload: stockId.slice(0, -1)}); // CASO FRACIONARIO, PUXAR TBM AS INFORMACOES DO PAI
        this.store.dispatch({ type: StocksMarketAction.getInstrumentInfo, payload: stockId });
    }

    /**
     * Function that get instruments param data from stocksExchange.stocksMarket store
     * @param stockId Stock Identifier
     */
    public getInstrumentStoreData(stockId: string) {
        return this.store.select((state) => state.stocksExchange.stocksMarket.instruments)
            .pipe( 
                filter((instrument) => (instrument !== null && instrument[stockId] ? true : false)),
                map((instrument) => instrument[stockId])
            )
    }

    public getInstrumentStoreErrorData() {
        return this.stocksMarketEffects.getInstrument$
            .pipe(filter((item) => item.type.equals(StocksMarketAction.getInstrumentInfoError)))
    }

    /**
     * Function that dispatch a getQuote action in StocksMarketAction
     * @param stockId Stock Identifier
     */
    public requestQuoteData(stockId: string){
        this.store.dispatch({ type: StocksMarketAction.getQuote, payload: stockId});
    }

    /**
     * Function that get quote param data from stocksExchange.stocksMarket store
     * @param stockId Stock Identifier
     */
    public getQuoteStoreData(stockId: string): Observable<any>{
        return this.store.select((state) => state.stocksExchange.stocksMarket.quote)
            .pipe(
                filter((item) => item[stockId]),
                map((item) => item[stockId])
            )
    }

    /**
     * Function that dispatchs a toast generic error
     * @param error error payload
     */
    public dispatchGenericError(error){
        this.store.dispatch({ type: ApplicationMessagesAction.genericError, payload: error });
    }
}